import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { StationV2 } from '../../../types/dashboardV2';
import {
  getMobileStatusBadgeClass,
  getMobileStatusDotClass,
  formatStatus,
} from '../../../shared/utils/statusUtils';
import { formatUAH } from '../../../shared/utils/currency';

interface MobileStationCardProps {
  station: StationV2;
  onQrClick: (station: StationV2) => void;
  onCardClick: (station: StationV2) => void;
}

export const MobileStationCard = memo(function MobileStationCard({
  station,
  onQrClick,
  onCardClick,
}: MobileStationCardProps) {
  const { t } = useTranslation();
  const handleQrClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onQrClick(station);
  }, [onQrClick, station]);

  const handleCardClick = useCallback(() => {
    onCardClick(station);
  }, [onCardClick, station]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onCardClick(station);
    }
  }, [onCardClick, station]);

  return (
    <div
      className="mobile-station-card"
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={t('dashboard.mobileCards.stationAria', { id: station.idTag, status: formatStatus(station.status) })}
    >
      <div className="mobile-card-header">
        <div>
          <span className="mobile-card-id-label">{t('dashboard.mobileCards.idTag')}</span>
          <h3 className="mobile-card-id-value">{station.idTag}</h3>
        </div>
        <span className={`mobile-status-badge ${getMobileStatusBadgeClass(station.status)}`}>
          <span className={`mobile-status-dot ${getMobileStatusDotClass(station.status)}`} aria-hidden="true" />
          {formatStatus(station.status)}
        </span>
      </div>

      <div className="mobile-card-stats">
        <div className="mobile-stat-item">
          <div className="mobile-stat-icon" aria-hidden="true">
            <span className="material-symbols-outlined">payments</span>
          </div>
          <div>
            <p className="mobile-stat-label">{t('dashboard.mobileCards.price')}</p>
            <p className="mobile-stat-value" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatUAH(station.price)}</p>
          </div>
        </div>
        <div className="mobile-stat-item">
          <div className="mobile-stat-icon" aria-hidden="true">
            <span className="material-symbols-outlined">ev_charger</span>
          </div>
          <div>
            <p className="mobile-stat-label">{t('dashboard.mobileCards.connectors')}</p>
            <p className="mobile-stat-value" style={{ fontVariantNumeric: 'tabular-nums' }}>{station.connectors}</p>
          </div>
        </div>
      </div>

      <div className="mobile-card-footer">
        <div className="mobile-card-address">
          <span className="material-symbols-outlined" aria-hidden="true">location_on</span>
          <span className="mobile-card-address-text">{station.address || t('dashboard.mobileCards.noAddress')}</span>
        </div>
        {station.organizationName && (
          <p className="org-text-muted" style={{ fontSize: 12, margin: '2px 0 0' }}>
            {t('dashboard.tables.stations.organization')}: {station.organizationName}
          </p>
        )}
        <button
          type="button"
          className="mobile-qr-btn"
          onClick={(e) => handleQrClick(e)}
          aria-label={t('dashboard.mobileCards.downloadQrAria', { id: station.idTag })}
        >
          <span className="material-symbols-outlined" aria-hidden="true">qr_code_2</span>
        </button>
      </div>
    </div>
  );
});

export default MobileStationCard;
